import { createRoot, type Root } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import AnalyticsControls from './AnalyticsControls'
import FoundingTurnstile from './FoundingTurnstile'
import {
  captureFoundingReferral,
  fetchFoundingConfig,
  fetchFoundingDashboard,
  requestFoundingAccessLink,
  submitFoundingSignup,
  verifyFoundingCode,
} from './api'
import { trackFoundingEvent } from './analytics'
import {
  closeCheckEmailDialog, deepActiveElement, inboxFor, openCheckEmailDialog,
  type CodeOutcome, type ResendOutcome, type ShareStep,
} from './checkEmailDialog'
import { DASHBOARD_WELCOME_KEY } from './dashboardIntent'
import { suggestEmail, suggestionLabel } from './emailTypos'
import { buildShareMessage, isFoundingSignupOpen, readFoundingAttribution } from './helpers'
import { describeLinkOutcome, describeRequestFailure } from './linkOutcomeCopy'
import { analyticsContext, captureBehavior, captureMetaSignup, setAnalyticsPurpose } from './privacyAnalytics'
import { lastArticleSlug, sessionAttribution, signupArticleSlug, signupAttributionPayload } from './attribution'
import { trackWebsiteSignup } from './websiteAnalytics'
import type { FoundingCampaignConfig, FoundingLinkOutcome, FoundingSignupResult, SourceType } from './types'
import './privacy-analytics.css'

declare global {
  interface Window { aeloLifestyleCleanup?: () => void }
}

type FormState = {
  form: HTMLFormElement
  input: HTMLInputElement
  submit: HTMLButtonElement
  submitHtml: string
  status: HTMLElement
  actions: HTMLDivElement
  configGate: boolean
  security: HTMLDivElement | null
  securityRoot: Root | null
  securityKey: string | null
  token: string | null
  tokenWaiters: Set<(token: string | null) => void>
  challengeAttempt: number
  signup: boolean
}

const CONFIG_DISPLAY_FRESHNESS_MS = 120_000
const CONFIG_RECOVERY_DELAYS_MS = [1500, 4000, 10_000]

/** Bind the reviewed static design to the existing production campaign contract. */
function initializeLifestyleLanding() {
  window.aeloLifestyleCleanup?.()
  const abort = new AbortController()
  const { signal } = abort
  const mounts: Root[] = []
  const added: Element[] = []
  const forms: FormState[] = []
  const attribution = readFoundingAttribution(location.search)
  const initialQuery = new URLSearchParams(location.search)
  let campaign: FoundingCampaignConfig | null = null
  let configPromise: Promise<FoundingCampaignConfig | null> | null = null
  let configFailed = false
  let configReceivedAt = 0
  let serverNow = 0
  let busy = false
  let configRecoveryTimer: number | null = null
  let configRecoveryAttempt = 0
  let configRecoveryExhausted = false
  let referralState: 'idle' | 'pending' | 'captured' | 'invalid' | 'unavailable' | 'skipped' = attribution.refCode ? 'pending' : 'idle'
  let referralPromise: Promise<void> | null = null
  let completedEmail: string | null = null
  // Addresses the person said are right after a "Did you mean...?" (lower-cased), so they're never asked twice.
  const typoConfirmed = new Set<string>()
  const countdown = document.getElementById('prelaunch-countdown') as HTMLTimeElement | null
  const countdownLabel = document.querySelector<HTMLElement>('[data-countdown-label]')

  const style = document.createElement('style')
  style.textContent = `
    .lifestyle-live-security{max-width:100%;overflow:hidden;margin-top:6px}
    .lifestyle-live-security .founding-turnstile[data-passed=true]>div:first-child{display:none}
    .lifestyle-live-security button,.lifestyle-live-actions button{font:inherit;color:inherit;border:0;background:transparent;padding:7px 0;cursor:pointer;text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:4px}
    .lifestyle-live-actions{display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:8px 20px;font-size:13px;line-height:1.5}
    .lifestyle-live-actions:empty{display:none}
    .lifestyle-live-actions a{text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:4px}
    .signup-form [data-live-outcome]:focus{outline:none}
    .lifestyle-live-actions button:disabled{opacity:.5;cursor:wait}
    .lifestyle-live-actions :focus-visible{outline:2px solid currentColor;outline-offset:4px}
    .signup-form [data-live-outcome]{font-size:16px;line-height:1.5;margin:14px 0 10px}
    .signup-form button[type=submit]:disabled,#signin-form button[type=submit]:disabled{cursor:wait;opacity:.65}
    .lifestyle-live-actions:has(.is-strong){gap:12px;margin-top:12px}
    .signup-form [data-typo]{font-size:20px;font-weight:700;line-height:1.35;margin:16px 0 4px}
    .lifestyle-live-actions .is-strong{font-size:17px;font-weight:700;min-height:48px;padding:10px 18px;border:2px solid currentColor;border-radius:0;text-decoration:none}
    .lifestyle-live-actions .is-strong:first-child{background:#092e3e;color:#fff;border-color:#092e3e}
    .aelo-invite-banner{box-sizing:border-box;padding:18px 20px;background:#e6f4ec;border-bottom:3px solid #2f7d55;color:#092e3e;text-align:center;font-size:18px;line-height:1.45}
    .aelo-invite-banner p{margin:0 auto;max-width:40em}
    .aelo-invite-banner strong{font-size:21px}
    .aelo-invite-banner .aelo-invite-banner__small{margin-top:6px;font-size:15px;color:#2b4752}
    .aelo-invite-banner a{display:inline-flex;align-items:center;justify-content:center;min-height:52px;margin-top:12px;padding:0 26px;background:#092e3e;color:#fff;font-size:18px;font-weight:700;text-decoration:none}
    .aelo-invite-banner a:focus-visible{outline:3px solid #2f7d55;outline-offset:3px}
  `
  document.head.append(style)
  added.push(style)

  if (document.body.dataset.aeloSharedPrivacy !== 'static') {
    const privacy = document.createElement('div')
    privacy.id = 'aelo-lifestyle-privacy'
    privacy.className = 'ph-no-capture'
    document.body.append(privacy)
    const privacyRoot = createRoot(privacy)
    privacyRoot.render(<BrowserRouter><AnalyticsControls /></BrowserRouter>)
    mounts.push(privacyRoot)
    added.push(privacy)
  }
  document.body.dataset.aeloLandingState = 'loading'

  function currentTime() { return serverNow ? serverNow + performance.now() - configReceivedAt : Date.now() }
  function configFresh() { return Boolean(campaign) && performance.now() - configReceivedAt < CONFIG_DISPLAY_FRESHNESS_MS }
  function configUnavailable() { return campaign ? !configFresh() : configFailed }
  function signupOpen() {
    if (!campaign || !configFresh() || !isFoundingSignupOpen(campaign)) return false
    const cutoff = Date.parse(campaign.submissionsCutoff ?? '')
    return !Number.isFinite(cutoff) || currentTime() < cutoff
  }
  function updateAvailability() {
    if (signal.aborted) return
    const open = signupOpen()
    document.body.dataset.aeloLandingState = configUnavailable() ? 'unavailable' : !campaign ? 'loading' : open ? 'ready' : 'closed'
    forms.forEach(state => {
      state.submit.disabled = busy || (state.signup && (!open || referralState === 'pending'))
      state.input.disabled = busy
      state.actions.querySelectorAll('button').forEach(button => { button.disabled = busy })
      state.submit.innerHTML = busy ? (state.signup ? 'Joining…' : 'Sending…') : state.submitHtml
    })
  }
  function status(state: FormState, message: string, error = false) {
    state.configGate = false
    delete state.status.dataset.configGate
    state.status.textContent = message
    delete state.status.dataset.typo
    state.status.dataset.liveOutcome = error ? 'error' : 'notice'
    state.status.setAttribute('data-private', '')
    state.status.classList.toggle('is-error', error)
  }
  function action(state: FormState, label: string, handler: () => void, strong = false) {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = label
    if (strong) button.className = 'is-strong'
    button.addEventListener('click', handler, { signal })
    state.actions.append(button)
    return button
  }
  /** Owner, 2026-10-12: someone opening a friend's link sees, at the top of the page, that a friend invited them. */
  function showInviteBanner() {
    if (document.getElementById('aelo-invite-banner')) return
    const target = forms.find(state => state.signup)
    const banner = document.createElement('div')
    banner.id = 'aelo-invite-banner'
    banner.className = 'aelo-invite-banner'
    banner.setAttribute('role', 'status')
    const lead = document.createElement('p')
    const strong = document.createElement('strong')
    strong.textContent = 'Your friend invited you!'
    lead.append(strong, ' Join free and get early access. Then invite your own friends and get free stuff too.')
    const small = document.createElement('p')
    small.className = 'aelo-invite-banner__small'
    small.textContent = 'Your friend only gets credit after you confirm your email.'
    banner.append(lead, small)
    if (target) {
      const join = document.createElement('a')
      join.href = `#${target.form.id}`
      join.textContent = 'Join free'
      join.addEventListener('click', event => {
        event.preventDefault()
        target.form.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' })
        target.input.focus({ preventScroll: true })
      }, { signal })
      banner.append(join)
    }
    const main = document.querySelector('main')
    if (main) main.prepend(banner)
    else document.body.prepend(banner)
    added.push(banner)
    clearFloatingHeader(banner)
  }
  /**
   * The homepage header floats over the top of the page and the first section pads itself to clear it. The banner
   * goes below the header instead of under it, and that section gives back the room it kept for the header.
   */
  function clearFloatingHeader(banner: HTMLElement) {
    const header = document.querySelector<HTMLElement>('.aelo-site-header')
    const next = banner.nextElementSibling instanceof HTMLElement ? banner.nextElementSibling : null
    const fit = () => {
      banner.style.marginTop = ''
      if (next) next.style.paddingTop = ''
      if (!header || getComputedStyle(header).position !== 'absolute') return
      const overlap = Math.round(header.getBoundingClientRect().bottom - banner.getBoundingClientRect().top)
      if (overlap <= 0) return
      banner.style.marginTop = `${overlap}px`
      if (next) next.style.paddingTop = `${Math.max(24, (parseFloat(getComputedStyle(next).paddingTop) || 0) - overlap)}px`
    }
    fit()
    addEventListener('resize', fit, { signal })
    signal.addEventListener('abort', () => { if (next) next.style.paddingTop = '' })
  }
  /** "Did you mean maria@gmail.com?" before sending to an address that can never confirm. */
  function offerCorrection(state: FormState, typed: string, suggestion: string) {
    state.actions.replaceChildren()
    status(state, `Did you mean ${suggestion}?`)
    state.status.dataset.typo = ''
    const label = suggestionLabel(suggestion)
    const yes = action(state, 'Yes, use that', () => {
      state.input.value = suggestion
      captureBehavior('aelo_typo_suggested', { source: state.form.id, destination: label, accepted: true })
      state.actions.replaceChildren()
      void submit(state)
    }, true)
    action(state, 'No, mine is right', () => {
      typoConfirmed.add(typed.toLowerCase())
      captureBehavior('aelo_typo_suggested', { source: state.form.id, destination: label, accepted: false })
      state.actions.replaceChildren()
      void submit(state)
    }, true)
    yes.focus()
  }
  function updateCountdown() {
    // Signup state still needs refreshing when shared chrome owns the countdown.
    const wasUnavailable = document.body.dataset.aeloLandingState === 'unavailable'
    updateAvailability()
    if (!wasUnavailable && configUnavailable()) showGate()
    if (!countdown || !countdownLabel) return
    const end = Date.parse(campaign?.submissionsCutoff ?? '')
    const seconds = Math.max(0, Math.ceil((end - currentTime()) / 1000))
    if (signupOpen() && Number.isFinite(seconds)) {
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor(seconds / 3600) % 24
      const minutes = Math.floor(seconds / 60) % 60
      const remaining = seconds % 60
      countdown.hidden = false
      countdown.dateTime = campaign!.submissionsCutoff!
      countdown.title = new Date(end).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })
      countdown.textContent = `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(remaining).padStart(2, '0')}s`
      countdown.setAttribute('aria-label', `${days} days, ${hours} hours, ${minutes} minutes, ${remaining} seconds remaining`)
      countdownLabel.textContent = 'AELO PRE-LAUNCH CLOSES IN'
    } else {
      countdown.hidden = true
      countdownLabel.textContent = configUnavailable() ? 'AELO PRE-LAUNCH' : !campaign ? 'AELO PRE-LAUNCH' : campaign.lifecycleStatus === 'preopening' ? 'AELO PRE-LAUNCH OPENS SOON' : 'AELO PRE-LAUNCH IS CLOSED'
    }
  }
  function lifecycleMessage(unavailable = configUnavailable()) {
    if (unavailable) return 'We couldn’t check pre-launch availability. Please try again.'
    if (campaign?.lifecycleStatus === 'preopening') return 'Pre-launch signups are not open yet. Please check back soon.'
    if (campaign?.lifecycleStatus === 'delayed' || campaign?.lifecycleStatus === 'unavailable') return 'Pre-launch signups are temporarily unavailable. Please try again shortly.'
    return 'Pre-launch signups have closed. Already joined? You can still request your sign-in link.'
  }
  function showGate(target?: FormState, unavailable = configUnavailable()) {
    if (!target && unavailable && !configRecoveryExhausted) return
    const state = target ?? forms.find(item => item.signup && item.form.contains(document.activeElement))
      ?? forms.find(item => item.form.id === 'closing-join') ?? forms.find(item => item.signup)
    if (!state || (!target && completedEmail)) return
    state.actions.replaceChildren()
    status(state, lifecycleMessage(unavailable), unavailable)
    state.configGate = true
    state.status.dataset.configGate = 'true'
    if (unavailable || campaign?.lifecycleStatus === 'delayed' || campaign?.lifecycleStatus === 'unavailable') {
      action(state, 'Retry', () => { void refreshConfig() })
    }
  }
  function clearConfigGates() {
    forms.filter(state => state.configGate).forEach(state => {
      state.actions.replaceChildren()
      status(state, '')
    })
  }
  function showReferralState() {
    forms.filter(state => state.signup).forEach(state => {
      if (completedEmail || !signupOpen()) return
      state.actions.replaceChildren()
      if (referralState === 'captured') status(state, 'Your friend’s invitation is saved.')
      else if (referralState === 'invalid') status(state, 'This invitation link is not valid. You can still join.')
      else if (referralState === 'unavailable') {
        status(state, 'We couldn’t check your friend’s invitation. Retry to keep their referral credit, or continue without it.', true)
        action(state, 'Retry invitation check', () => { void captureReferral() })
        action(state, 'Continue without invitation', () => {
          referralState = 'skipped'
          showReferralState()
          updateAvailability()
        })
      } else status(state, referralState === 'pending' ? 'Checking your friend’s invitation…' : '')
    })
  }
  async function captureReferral(): Promise<void> {
    if (!attribution.refCode || referralState === 'captured' || referralState === 'invalid' || referralState === 'skipped') return
    if (referralPromise) return referralPromise
    referralState = 'pending'
    showReferralState()
    updateAvailability()
    referralPromise = captureFoundingReferral(attribution.refCode).then(result => {
      if (signal.aborted) return
      referralState = result.valid ? 'captured' : 'invalid'
      if (result.valid) {
        // Audit 2026-10-02: the invite code stays in the address. In Instagram, Facebook, Gmail and LinkedIn in-app browsers,
        // "Open in browser" and "Copy link" use the current address; removing ?ref= there lost the friend's credit whenever
        // the visitor finished joining in Safari or Chrome (cookies are not shared between the two). The code is the
        // inviter's public share code, never sent to analytics (attribution.ts keeps only invite_link: true).
        trackFoundingEvent('referral_cookie_captured', {
          onceKey: `referral_cookie:${attribution.refCode}`,
          properties: { landing_page_variant: campaign?.copy.activeHeadlineKey, source: 'lifestyle' },
        })
        showInviteBanner()
      }
    }).catch(() => { if (!signal.aborted) referralState = 'unavailable' }).finally(() => {
      referralPromise = null
      if (!signal.aborted) { showReferralState(); updateAvailability() }
    })
    return referralPromise
  }
  function scheduleConfigRecovery() {
    if (signal.aborted || configRecoveryTimer !== null || configRecoveryAttempt >= CONFIG_RECOVERY_DELAYS_MS.length) return
    const delay = CONFIG_RECOVERY_DELAYS_MS[configRecoveryAttempt++]
    configRecoveryTimer = window.setTimeout(() => {
      configRecoveryTimer = null
      if (!signal.aborted) void refreshConfig()
    }, delay)
  }
  function refreshConfig(): Promise<FoundingCampaignConfig | null> {
    if (configPromise) return configPromise
    if (configRecoveryTimer !== null) { window.clearTimeout(configRecoveryTimer); configRecoveryTimer = null }
    const requestAbort = new AbortController()
    const onAbort = () => requestAbort.abort()
    signal.addEventListener('abort', onAbort, { once: true })
    const timeout = window.setTimeout(() => requestAbort.abort(), 15_000)
    configPromise = (async () => {
      try {
        const next = await fetchFoundingConfig(requestAbort.signal)
        if (signal.aborted) return null
        const wasUnavailable = configUnavailable() || !campaign || !signupOpen()
        campaign = next
        configFailed = false
        configRecoveryAttempt = 0
        configRecoveryExhausted = false
        configReceivedAt = performance.now()
        serverNow = Date.parse(next.serverNow ?? '') || Date.now()
        clearConfigGates()
        updateCountdown()
        if (!signupOpen()) showGate()
        else {
          if (wasUnavailable) showReferralState()
          void captureReferral()
          const focused = forms.find(state => state.input === document.activeElement)
          if (focused) mountSecurity(focused)
        }
        return next
      } catch {
        if (!signal.aborted) {
          configFailed = true
          configRecoveryExhausted = configRecoveryAttempt >= CONFIG_RECOVERY_DELAYS_MS.length
          updateCountdown()
          // A brief background outage does not replace recent campaign information.
          // Submission below still requires its own successful availability check.
          if (configUnavailable()) showGate()
          scheduleConfigRecovery()
        }
        return null
      } finally {
        window.clearTimeout(timeout)
        signal.removeEventListener('abort', onAbort)
        configPromise = null
      }
    })()
    return configPromise
  }
  function resetSecurity(state: FormState) {
    state.token = null
    state.challengeAttempt += 1
    state.securityKey = null
    for (const resolve of state.tokenWaiters) resolve(null)
    state.tokenWaiters.clear()
    if (state.securityRoot) { state.securityRoot.unmount(); state.securityRoot = null }
  }
  function mountSecurity(state: FormState) {
    const siteKey = campaign?.turnstileSiteKey
    if (!state.signup || !state.security || !siteKey || !signupOpen()) return
    if (state.securityRoot && state.securityKey === siteKey) return
    resetSecurity(state)
    state.securityRoot = createRoot(state.security)
    state.securityKey = siteKey
    state.securityRoot.render(<FoundingTurnstile key={state.challengeAttempt} siteKey={siteKey} onToken={token => {
      state.token = token
      if (token) {
        for (const resolve of state.tokenWaiters) resolve(token)
        state.tokenWaiters.clear()
      }
    }} />)
  }
  async function tokenFor(state: FormState) {
    if (!campaign?.turnstileSiteKey) return null
    mountSecurity(state)
    if (state.token) return state.token
    status(state, 'One moment, the security check is finishing…')
    return new Promise<string | null>(resolve => {
      const finish = (token: string | null) => {
        window.clearTimeout(timeout)
        state.tokenWaiters.delete(finish)
        resolve(token)
      }
      const timeout = window.setTimeout(() => finish(state.token), 20_000)
      state.tokenWaiters.add(finish)
    })
  }
  function retryText(retryAt?: string | null) {
    const timestamp = Date.parse(retryAt ?? '')
    if (!Number.isFinite(timestamp)) return 'a little later'
    const minutes = Math.max(1, Math.ceil((timestamp - currentTime()) / 60_000))
    return minutes < 60 ? `in about ${minutes} minute${minutes === 1 ? '' : 's'}` : `at ${new Date(timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
  }
  function outcomeText(email: string, link?: FoundingLinkOutcome, retryAt?: string | null) {
    return describeLinkOutcome(link, email, retryText(retryAt))
  }
  function showSignupResult(email: string, result: FoundingSignupResult, submitted: FormState, origin: Element | null) {
    completedEmail = email
    const isNew = result.member !== 'existing'
    // The code step shows only once the server says the email carries a code (its release switch). The server says so
    // for everyone still confirming, new or returning; a confirmed member gets a sign-in link and no code.
    const withCode = result.confirmationCode === true
    forms.filter(state => state.signup).forEach(state => {
      state.actions.replaceChildren()
      const warning = outcomeText(email, result.link, result.retryAt)
      // Ask Aelo watches for "Check your inbox" at the start of this line: keep it there.
      status(state, warning ?? (withCode
        ? `Check your inbox. We sent a 6-digit code to ${email}. Type it in to get your free invite link.`
        : isNew
          ? `Check your inbox. Tap the button in the email we sent to ${email} to get your free invite link.`
          : `Check your inbox. We sent a link to ${email}. Open it to see your free invite link.`), Boolean(warning))
      if (withCode && result.link === 'sent') action(state, 'Type my code', () => showCheckEmail(state, email, result.member, null, true), true)
      action(state, withCode ? 'Send a new code' : 'Send the link again', () => { void resend(state, email) })
      action(state, 'Use a different email', () => {
        completedEmail = null
        forms.filter(item => item.signup).forEach(item => { item.actions.replaceChildren(); status(item, ''); item.input.value = '' })
        state.input.focus()
      })
      const dashboard = document.createElement('a')
      dashboard.href = '/founding/refer'
      dashboard.textContent = 'Already confirmed? Open my page'
      state.actions.append(dashboard)
    })
    // Owner, 2026-10-08: a pop-up that says "Go check your email", over the page, when the server says it sent a link.
    // Otherwise the form's own line stays the only message (never "sent" when nothing was).
    if (result.link === 'sent') showCheckEmail(submitted, email, result.member, origin, withCode)
  }
  /** After a correct code, every signup form says so and points to the member's page, share step open. */
  function showConfirmed() {
    forms.filter(state => state.signup).forEach(state => {
      state.actions.replaceChildren()
      status(state, 'You’re in! Your free invite link is ready.')
      const page = document.createElement('a')
      page.href = '/founding/refer?open=share'
      page.textContent = 'Open my page and send my link'
      state.actions.append(page)
    })
  }
  function showCheckEmail(state: FormState, email: string, member: FoundingSignupResult['member'], origin: Element | null, withCode: boolean) {
    // The provider is a fixed label (gmail, outlook, yahoo, icloud, aol or none), never the address or its domain.
    const provider = inboxFor(email)?.provider ?? 'other'
    openCheckEmailDialog({
      email, origin, returnFocus: state.status,
      // A new signup confirms with the code. Anyone else gets the line that is true for a sign-in link and for a fresh
      // confirmation link alike (brand review 2026-10-08).
      member: member === 'new' ? 'new' : 'existing',
      onShown: shown => captureBehavior('aelo_check_email_shown', { source: state.form.id, destination: shown, status: member ?? 'unknown', ...articleProperties(state) }),
      onInbox: shown => captureBehavior('aelo_check_email_inbox_clicked', { source: state.form.id, destination: shown, ...articleProperties(state) }, { sendInstantly: true }),
      // "Send a new code" is the form's own "Send a new code": the same request, and the form's line updates too.
      onResend: () => resend(state, email),
      onVerify: !withCode ? undefined : async (code): Promise<CodeOutcome> => {
        try {
          const result = await verifyFoundingCode(email, code)
          captureBehavior('aelo_code_submitted', { source: state.form.id, result: result.status, destination: provider, ...articleProperties(state) }, { sendInstantly: result.status === 'ok' })
          if (result.status === 'ok') {
            completedEmail = email
            showConfirmed()
            // The member page greets them once ("You're in! Now let's get you free stuff."), as after the email's button.
            try { sessionStorage.setItem(DASHBOARD_WELCOME_KEY, '1') } catch { /* Storage blocked: no greeting, nothing else changes. */ }
            return { outcome: 'ok' }
          }
          if (result.status === 'invalid') return { outcome: 'invalid', attemptsLeft: result.attemptsLeft }
          if (result.status === 'expired') return { outcome: 'expired' }
          return { outcome: 'limited', retryAt: result.retryAt }
        } catch (error) {
          captureBehavior('aelo_code_submitted', { source: state.form.id, result: 'error', destination: provider, ...articleProperties(state) })
          return { outcome: 'failed', message: describeRequestFailure(error, 'We couldn’t check that code. Check your connection and try again.') }
        }
      },
      onConfirmed: async (): Promise<ShareStep | null> => {
        const dashboard = await fetchFoundingDashboard()
        if (dashboard.sharingLocked || !dashboard.referralUrl) return null
        return { link: dashboard.referralUrl, message: buildShareMessage(dashboard.referralUrl), pageUrl: '/founding/refer?open=share' }
      },
      onShare: channel => {
        if (channel === 'copy') trackFoundingEvent('referral_link_copied', { channel: 'copy', properties: { source: 'popup' } })
        else trackFoundingEvent('referral_share_clicked', { channel, properties: { source: 'popup' } })
      },
      onShareShown: () => captureBehavior('aelo_share_step_shown', { source: 'popup' }),
    })
  }
  async function resend(state: FormState, email: string): Promise<ResendOutcome | null> {
    if (busy) return null
    busy = true
    state.actions.querySelectorAll('button').forEach(button => { button.disabled = true })
    updateAvailability()
    status(state, 'Sending…')
    try {
      const outcome = await requestFoundingAccessLink(email)
      if (signal.aborted) return null
      const warning = outcomeText(email, outcome.link, outcome.retryAt)
      status(state, warning ?? `Check your inbox. A new email is on its way to ${email}.`, Boolean(warning))
      // The pop-up says "Sent." only when the server sent one. The proxy leaves `link` out when it is "sent" and names
      // every other outcome, which describeLinkOutcome turns into the warning.
      return warning ? { outcome: 'not_sent', message: warning } : { outcome: 'sent' }
    } catch (error) {
      if (signal.aborted) return null
      const message = describeRequestFailure(error, 'Your link could not be sent. Please try again.')
      status(state, message, true)
      return { outcome: 'failed', message }
    }
    finally {
      busy = false
      if (!signal.aborted) { state.actions.querySelectorAll('button').forEach(button => { button.disabled = false }); updateAvailability() }
    }
  }
  /** Release 14: the article a signup is credited to (the article's own form, ?article=, or a recent article link). */
  function articleFor(state: FormState) {
    return signupArticleSlug(state.form.dataset.articleSlug, initialQuery.get('article'))
  }
  function articleProperties(state: FormState) {
    const article = articleFor(state), read = lastArticleSlug()
    return { ...(article ? { article_slug: article } : {}), ...(read ? { last_article_slug: read } : {}) }
  }
  /** Release 14: the signup's source type from the session's latest landing, not only this page's address. */
  function signupSourceType(): SourceType {
    if (referralState === 'captured') return 'referral'
    const requested = initialQuery.get('source_type')
    if (requested === 'organic' || requested === 'paid' || requested === 'seeded_email' || requested === 'creator') return requested
    const last = sessionAttribution()?.last
    if (last?.paid) return 'paid'
    if (last?.referrerKind === 'email' && last.utmSource) return 'seeded_email'
    if (/^(?:creator|influencer)$/i.test(last?.utmMedium ?? '')) return 'creator'
    return 'organic'
  }
  async function submit(state: FormState) {
    if (busy) return
    // Where the person pressed join (the form, or Ask Aelo's panel), read before the form is disabled.
    const origin = deepActiveElement()
    const email = state.input.value.trim()
    if (!email || !state.input.validity.valid) {
      state.input.setAttribute('aria-invalid', 'true')
      status(state, 'Please enter a valid email address.', true)
      state.input.focus()
      return
    }
    state.input.removeAttribute('aria-invalid')
    if (!state.signup) { await resend(state, email); return }
    // Owner, 2026-10-12: catch typos before they become an address that can never confirm.
    const suggestion = typoConfirmed.has(email.toLowerCase()) ? null : suggestEmail(email)
    if (suggestion) { offerCorrection(state, email, suggestion); return }
    busy = true
    updateAvailability()
    status(state, 'One moment…')
    captureBehavior('aelo_signup_cta', { source: state.form.id, stage: 'started', ...articleProperties(state) })
    let requestStarted = false
    try {
      const freshCampaign = await refreshConfig()
      if (signal.aborted) return
      if (!freshCampaign) { showGate(state, true); return }
      if (!signupOpen()) { showGate(state); return }
      await captureReferral()
      if (signal.aborted) return
      if (referralState === 'unavailable') { showReferralState(); return }
      const token = await tokenFor(state)
      if (signal.aborted) return
      if (campaign?.turnstileSiteKey && !token) throw new Error('Please finish the security check, then try again.')
      if (!signupOpen() || !campaign) { showGate(state); return }
      requestStarted = true
      const articleSlug = articleFor(state)
      trackWebsiteSignup('started', undefined, articleSlug)
      // Release 14: attribution comes from this tab's session (first and latest landing), so a visitor who arrived
      // from an ad, a search engine or an AI assistant keeps that source after moving between pages.
      const last = sessionAttribution()?.last
      const result = await submitFoundingSignup({
        email,
        ageConfirmed: true,
        marketingOptIn: true,
        sourceType: signupSourceType(),
        landingPageVariant: campaign.copy.activeHeadlineKey,
        destinationPath: location.pathname,
        articleSlug,
        signupFormId: state.form.id,
        creativeId: last?.utmContent ?? attribution.utm.content,
        platformCampaignId: last?.platformCampaignId ?? initialQuery.get('campaign_id') ?? undefined,
        platformAdsetId: last?.platformAdsetId ?? initialQuery.get('adset_id') ?? undefined,
        platformAdId: last?.platformAdId ?? initialQuery.get('ad_id') ?? undefined,
        clickId: last?.clickId,
        utm: last ? { source: last.utmSource, medium: last.utmMedium, campaign: last.utmCampaign, content: last.utmContent } : attribution.utm,
        attribution: signupAttributionPayload(),
        ...(token ? { turnstileToken: token } : {}),
      }) as FoundingSignupResult | { status: 'existing_session'; redirectTo?: string }
      if (signal.aborted) return
      // Preserve the older API's signed-in shortcut during a rolling release.
      if (result.status === 'existing_session' && result.redirectTo === '/founding/refer') {
        trackWebsiteSignup('accepted', result.status, articleSlug)
        captureBehavior('aelo_signup_cta', { source: state.form.id, stage: 'accepted', ...articleProperties(state) }, { sendInstantly: true })
        location.assign('/founding/refer')
        return
      }
      if (result.status !== 'check_inbox') throw new Error('We couldn’t confirm your signup. Please try again.')
      trackWebsiteSignup('accepted', result.status, articleSlug)
      captureMetaSignup(result)
      captureBehavior('aelo_signup_cta', { source: state.form.id, stage: 'accepted', ...articleProperties(state) })
      showSignupResult(email, result, state, origin)
    } catch (error) {
      if (!signal.aborted) {
        if (requestStarted) trackWebsiteSignup('failed', undefined, articleFor(state))
        captureBehavior('aelo_signup_cta', { source: state.form.id, stage: 'failed', ...articleProperties(state) })
        status(state, describeRequestFailure(error, 'Aelo could not add your email. Please try again.'), true)
      }
    } finally {
      resetSecurity(state)
      busy = false
      updateAvailability()
    }
  }

  document.querySelectorAll<HTMLFormElement>('form.signup-form, #signin-form').forEach(form => {
    const { id } = form
    const input = form.querySelector<HTMLInputElement>('input[type=email]')
    const button = form.querySelector<HTMLButtonElement>('button[type=submit]')
    const message = form.querySelector<HTMLElement>('.form-status')
    const row = form.querySelector<HTMLElement>('.signup-row')
    if (!id || !input || !button || !message || (id !== 'signin-form' && !row)) return
    form.setAttribute('data-private', '')
    const actions = document.createElement('div')
    actions.className = 'lifestyle-live-actions'
    message.insertAdjacentElement('afterend', actions)
    added.push(actions)
    const state: FormState = { form, input, submit: button, submitHtml: button.innerHTML, status: message, actions,
      configGate: false, security: null, securityRoot: null, securityKey: null, token: null,
      tokenWaiters: new Set(), challengeAttempt: 0, signup: id !== 'signin-form' }
    if (state.signup && row) {
      const security = document.createElement('div')
      security.className = 'lifestyle-live-security'
      row.insertAdjacentElement('afterend', security)
      state.security = security
      added.push(security)
    }
    forms.push(state)
    form.addEventListener('submit', event => { event.preventDefault(); void submit(state) }, { signal })
    input.addEventListener('input', () => { input.removeAttribute('aria-invalid') }, { signal })
    input.addEventListener('focus', () => {
      if (!state.signup) return
      if (signupOpen()) mountSecurity(state)
      else if (!configPromise) void refreshConfig()
      trackFoundingEvent('prelaunch_email_submit_started', {
        onceKey: `lifestyle_email_focus:${id}`, persist: false,
        properties: { source: id, landing_page_variant: campaign?.copy.activeHeadlineKey, ...articleProperties(state) },
      })
    }, { signal })
  })

  document.querySelector('.dialog-close')?.setAttribute('aria-label', 'Close sign-in')
  updateAvailability()
  updateCountdown()
  void refreshConfig()
  const clockTimer = window.setInterval(updateCountdown, 1000)
  const refreshTimer = window.setInterval(() => { if (document.visibilityState === 'visible' && !busy) void refreshConfig() }, 30_000)
  window.addEventListener('focus', () => { if (!busy) void refreshConfig() }, { signal })
  window.addEventListener('online', () => { if (!busy) void refreshConfig() }, { signal })
  if (initialQuery.get('returning') === '1') {
    const dialog = document.getElementById('signin-dialog') as HTMLDialogElement | null
    if (dialog && !dialog.open) dialog.showModal()
    document.getElementById('signin-email')?.focus()
  }
  const cleanup = () => {
    if (signal.aborted) return
    abort.abort()
    window.clearInterval(clockTimer)
    window.clearInterval(refreshTimer)
    if (configRecoveryTimer !== null) window.clearTimeout(configRecoveryTimer)
    forms.forEach(resetSecurity)
    closeCheckEmailDialog()
    mounts.forEach(root => root.unmount())
    added.forEach(element => element.remove())
  }
  window.aeloLifestyleCleanup = cleanup
  if (import.meta.hot) import.meta.hot.dispose(cleanup)
}

// The same consent-aware bridge the research pages expose (staticAnalytics.tsx), so page add-ons such as Ask Aelo send
// their events through this page's analytics layer instead of loading their own.
window.AeloPrelaunchAnalytics ??= { context: analyticsContext, capture: captureBehavior, setPurpose: setAnalyticsPurpose }
initializeLifestyleLanding()
